/**
 * 보고서의 고객(브랜드) 고르기 — 버튼을 누르면 선택 창이 열린다 (운영자 결정 2026-10-11).
 * 예전에는 제목 옆의 맨 `<select>` 였다 — 고객이 많으면 찾기 어렵고, 고른 고객이 제목처럼 보이지 않았다.
 *
 * 창 안에서 이름·브랜드명으로 거른다. 결과가 하나면 Enter 로 고른다.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Dialog } from './ui/dialog.tsx';
import { Input } from './ui/form.tsx';

export interface PickItem {
  id: string;
  name: string;
  brandNames: string[];
}

export function BrandPicker({
  items,
  value,
  onChange,
  noun,
  big,
}: {
  items: readonly PickItem[];
  value: string;
  onChange: (id: string) => void;
  /** 「고객」 또는 「브랜드」 */
  noun: string;
  /** 아직 고르지 않았을 때 본문 가운데 큰 버튼으로 */
  big?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const current = items.find((c) => c.id === value);
  return (
    <>
      {big ? (
        <Button variant="live" size="lg" onClick={() => setOpen(true)}>
          {noun} 선택
        </Button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 font-medium text-slate-700 text-sm transition-colors hover:border-slate-400 hover:text-slate-900"
        >
          {current ? `${noun} 바꾸기` : `${noun} 선택`}
          <span aria-hidden className="text-slate-400 text-xs">
            ▾
          </span>
        </button>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} title={`${noun} 선택`}>
        <PickList
          items={items}
          value={value}
          noun={noun}
          onPick={(id) => {
            onChange(id);
            setOpen(false);
          }}
        />
      </Dialog>
    </>
  );
}

function PickList({
  items,
  value,
  noun,
  onPick,
}: {
  items: readonly PickItem[];
  value: string;
  noun: string;
  onPick: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  // 창이 열리면 바로 검색할 수 있게 — Dialog 는 열릴 때만 자식을 그린다
  useEffect(() => input.current?.focus(), []);
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? items.filter((c) => [c.name, ...c.brandNames].some((s) => s.toLowerCase().includes(needle)))
    : items;

  return (
    <div className="grid gap-3">
      <Input
        ref={input}
        placeholder={`${noun} 이름·브랜드명으로 찾기`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && shown.length === 1) {
            e.preventDefault();
            onPick(shown[0]!.id);
          }
        }}
      />
      {shown.length === 0 ? (
        <p className="py-6 text-center text-slate-500 text-sm">
          {items.length === 0 ? `볼 수 있는 ${noun}이 없습니다` : '찾는 이름이 없습니다'}
        </p>
      ) : (
        <ul className="-mx-1 max-h-[60vh] overflow-y-auto">
          {shown.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onPick(c.id)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50',
                  c.id === value && 'bg-live/10 hover:bg-live/10',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-900">{c.name}</span>
                  {c.brandNames.length && c.brandNames.join(' · ') !== c.name ? (
                    <span className="block truncate text-slate-500 text-xs">{c.brandNames.join(' · ')}</span>
                  ) : null}
                </span>
                {c.id === value ? <span className="shrink-0 text-live text-xs">보는 중</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
